import { ErrorOutline } from "@mui/icons-material";
import { Button, CircularProgress, InputBase } from "@mui/material";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle, ExternalLink } from "lucide-react";
import Logo from "../reusables/Logo";
import { isGoogleEmailForAddress, isLikelyValidEmail } from "../../utils/emailProvider";

const ForgotPassword = () => {
  const navigate = useNavigate()
  const [errorMsg, setErrormsg] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isGoogleEmail, setIsGoogleEmail] = useState(false);
  const [isCheckingProvider, setIsCheckingProvider] = useState(false);
  const [user, setUser] = useState({
    email:'',
  })

  // Gmail search URL for emails from hello@twilanswer.com in the last 24 hours
  const gmailSearchUrl = `https://mail.google.com/mail/u/0/#search/from:hello@twilanswer.com+newer_than:1d`;

  // Debounced Google Workspace/Gmail detection
  useEffect(() => {
    if (!user.email) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    if (!isLikelyValidEmail(user.email)) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    setIsCheckingProvider(true);
    const handle = setTimeout(async () => {
      try {
        const result = await isGoogleEmailForAddress(user.email);
        setIsGoogleEmail(result);
      } catch {
        setIsGoogleEmail(false);
      } finally {
        setIsCheckingProvider(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [user.email]);

   


    async function sendResetLink(){
      setErrormsg(null)
     
      setLoader(true);
      try {
        await axios.post(
          process.env.REACT_APP_BACKEND_URL + "email/send-reset-link",
          { email: user.email }
        );
        setIsSubmitted(true);
        setLoader(false);
      } catch (error) {
        // Log for production diagnostics when send-reset-link fails
        console.error("ForgotPassword: send reset link failed", error.response?.data || error.message || error);
        if (error.response && error.response.data) {
          setErrormsg(error.response.data.message);
        }
        setLoader(false);
      }
    }
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <div className="flex justify-center">
            <Logo size="md" />
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Check your email
            </h2>
            
            <p className="text-sm text-gray-600 mb-6">
              We've sent a password reset link to <strong>{user.email}</strong>
            </p>
            
            <div className="space-y-4">
              {isGoogleEmail && (
                <Button
                  fullWidth
                  onClick={() => {
                    window.open(gmailSearchUrl, "_blank", "noopener,noreferrer");
                  }}
                  startIcon={<ExternalLink size={16} />}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#16a34a",
                    color: "#fff",
                    borderRadius: "6px",
                    py: "10px",
                    fontSize: "14px",
                    "&:hover": {
                      backgroundColor: "#15803d",
                    },
                  }}
                >
                  Open Gmail
                </Button>
              )}
              
              <p className="text-xs text-gray-500">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setIsSubmitted(false);
                  setUser({...user, email: ''});
                  setIsGoogleEmail(false);
                  setIsCheckingProvider(false);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                  py: "10px",
                  borderColor: "#D1D5DB",
                  color: "#374151",
                  fontSize: "14px",
                  "&:hover": {
                    borderColor: "#9CA3AF",
                    bgcolor: "#F9FAFB",
                  },
                }}
              >
                Try different email
              </Button>
              
              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#605BFF] hover:bg-[#4f4adf] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#605BFF]"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <Logo size="md" />
        </div>
        
        {/* Heading */}
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Forgot your password?
        </h2>
        
        {/* Description */}
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10">
          {errorMsg && (
            <div className="text-center text-red-500 text-sm mt-2 flex justify-center items-center font-semibold mb-4">
              <ErrorOutline sx={{ fontSize: "16px", mr: "4px" }} /> {errorMsg}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); sendResetLink(); }} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <InputBase
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={user.email}
                  onChange={(e) => setUser({...user, email: e.target.value})}
                  disabled={loader}
                  placeholder="Enter your email address"
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #D1D5DB",
                    width: "100%",
                    pl: "40px",
                    pr: "12px",
                    py: "8px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    "&:focus": {
                      outline: "none",
                      ring: "2px",
                      ringColor: "#605BFF",
                      borderColor: "#605BFF",
                    },
                    "&:disabled": {
                      bgcolor: "#F9FAFB",
                      color: "#6B7280",
                    },
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <Button
                type="submit"
                fullWidth
                disabled={loader || !user.email}
                variant="contained"
                  color="primary"
                  sx={{
                  py: "10px",
                  px: "16px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    bgcolor: "#4f4adf",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}
              >
                {loader ? (
                  <CircularProgress size={"1rem"} sx={{ color: "white" }} />
                ) : (
                  "Send reset link"
                )}
              </Button>
            </div>
          </form>

          {/* Back to Login */}
          <div className="mt-6">
            <button
              onClick={() => navigate("/login")}
              className="flex items-center justify-center text-sm text-[#605BFF] hover:text-[#4f4adf] w-full"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
